import { useState } from 'react'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { X, Play, Pause, Loader2 } from 'lucide-react'

interface DeckActivationModalProps {
  deckId: Id<'flashcardDecks'>
  deckName: string
  currentStatus: 'active' | 'paused'
  onClose: () => void
  onStatusChanged?: (newStatus: 'active' | 'paused') => void
}

export function DeckActivationModal({
  deckId,
  deckName,
  currentStatus,
  onClose,
  onStatusChanged,
}: DeckActivationModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleDeckStatus = useMutation(api.flashcards.toggleDeckStatus)

  const isActive = currentStatus === 'active'
  const actionVerb = isActive ? 'Pause' : 'Activate'
  const actionVerbGerund = isActive ? 'Pausing' : 'Activating'

  const handleToggle = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await toggleDeckStatus({ deckId })
      onStatusChanged?.(result.newStatus as 'active' | 'paused')
      onClose()
    } catch (err) {
      console.error('Failed to toggle deck status:', err)
      setError(`Failed to ${actionVerb.toLowerCase()} deck. Please try again.`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-black border-4 border-sky-blue shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-sky-blue">
          <div className="flex items-center gap-3">
            {isActive ? (
              <Pause className="w-6 h-6 text-terminal-green" />
            ) : (
              <Play className="w-6 h-6 text-terminal-green" />
            )}
            <h2 className="text-xl font-bold text-sky-blue font-mono tracking-wider">
              [{actionVerb.toUpperCase()} DECK]
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-trail-tan hover:text-sky-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 border-2 border-red-500 bg-red-500/10">
              <p className="text-red-500 font-mono text-sm">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <div className="text-trail-tan font-mono text-sm mb-2">DECK NAME:</div>
            <div className="text-white font-mono text-lg font-bold">{deckName}</div>
          </div>

          <div className="mb-6 p-4 border-2 border-trail-tan bg-trail-tan/5">
            <div className="text-terminal-green font-mono text-sm font-bold mb-3">
              {actionVerbGerund.toUpperCase()} THIS DECK WILL:
            </div>
            {isActive ? (
              <ul className="space-y-2 text-trail-tan font-mono text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Hide all flashcards from this deck in your daily reviews</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Remove deck from calendar visualization</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Preserve your progress - you can reactivate anytime</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Deck will remain in your collection</span>
                </li>
              </ul>
            ) : (
              <ul className="space-y-2 text-trail-tan font-mono text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Include flashcards in your daily review queue</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Show deck in calendar visualization</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Resume where you left off with preserved progress</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-terminal-green">●</span>
                  <span>Deck color will appear in your learning calendar</span>
                </li>
              </ul>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleToggle}
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-terminal-green text-black font-bold font-mono border-4 border-terminal-green hover:bg-black hover:text-terminal-green transition-all retro-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{actionVerbGerund.toUpperCase()}...</span>
                </>
              ) : (
                <span>{actionVerb.toUpperCase()} DECK</span>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-6 py-3 border-4 border-sky-blue text-sky-blue font-bold font-mono hover:bg-sky-blue hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
